import { ApiResponse, CrmCallActivity, CrmCompany, CrmContact, CrmLink, CrmPayableActivity } from '../types';
import api from './apiClient';

export const crmAPI = {
  getOverview: async (search?: string): Promise<ApiResponse<{ contacts: CrmContact[]; companies: CrmCompany[]; stats: { contacts: number; companies: number; links: number; calls: number } }>> => {
    try {
      const response = await api.get('/crm/overview', { params: search ? { search } : undefined });
      return response.data;
    } catch (error) {
      const [contactsResponse, companiesResponse] = await Promise.all([
        api.get('/crm/contacts'),
        api.get('/crm/companies')
      ]);

      const normalizedSearch = String(search || '').trim().toLowerCase();
      const contacts = (contactsResponse.data?.contacts || []) as CrmContact[];
      const companies = (companiesResponse.data?.companies || []) as CrmCompany[];

      const filteredContacts = !normalizedSearch
        ? contacts
        : contacts.filter((contact) =>
            [
              contact.fullName,
              contact.email,
              contact.phone,
              contact.companyName,
              contact.position
            ]
              .filter(Boolean)
              .some((value) => String(value).toLowerCase().includes(normalizedSearch))
          );

      const filteredCompanies = !normalizedSearch
        ? companies
        : companies.filter((company) =>
            [
              company.name,
              company.email,
              company.phone,
              company.taxId,
              company.website
            ]
              .filter(Boolean)
              .some((value) => String(value).toLowerCase().includes(normalizedSearch))
          );

      return {
        success: true,
        message: 'CRM loaded via fallback',
        contacts: filteredContacts,
        companies: filteredCompanies,
        data: {
          contacts: filteredContacts,
          companies: filteredCompanies,
          stats: {
            contacts: contacts.length,
            companies: companies.length,
            links: filteredContacts.reduce((sum, contact) => sum + Number(contact.linksCount || 0), 0)
              + filteredCompanies.reduce((sum, company) => sum + Number(company.linksCount || 0), 0),
            calls: 0
          }
        }
      };
    }
  },

  getContacts: async (): Promise<ApiResponse<{ contacts: CrmContact[] }>> => {
    const response = await api.get('/crm/contacts');
    return response.data;
  },

  getContact: async (id: number): Promise<ApiResponse<{ contact: CrmContact; links: CrmLink[]; calls: CrmCallActivity[]; mailFollowups?: any[]; tasks?: any[]; payables?: CrmPayableActivity[]; followupSuggestions?: any[] }>> => {
    const response = await api.get(`/crm/contacts/${id}`);
    return response.data;
  },

  createContact: async (data: Partial<CrmContact> & { fullName: string }): Promise<ApiResponse<{ contact: CrmContact }>> => {
    const response = await api.post('/crm/contacts', data);
    return response.data;
  },

  updateContact: async (id: number, data: Partial<CrmContact>): Promise<ApiResponse<{ contact: CrmContact }>> => {
    const response = await api.put(`/crm/contacts/${id}`, data);
    return response.data;
  },

  deleteContact: async (id: number): Promise<ApiResponse> => {
    const response = await api.delete(`/crm/contacts/${id}`);
    return response.data;
  },

  getCompanies: async (): Promise<ApiResponse<{ companies: CrmCompany[] }>> => {
    const response = await api.get('/crm/companies');
    return response.data;
  },

  getCompany: async (id: number): Promise<ApiResponse<{ company: CrmCompany; contacts: CrmContact[]; links: CrmLink[]; calls: CrmCallActivity[]; payables: CrmPayableActivity[]; mailFollowups?: any[]; tasks?: any[]; followupSuggestions?: any[] }>> => {
    const response = await api.get(`/crm/companies/${id}`);
    return response.data;
  },

  createCompany: async (data: Partial<CrmCompany> & { name: string }): Promise<ApiResponse<{ company: CrmCompany }>> => {
    const response = await api.post('/crm/companies', data);
    return response.data;
  },

  updateCompany: async (id: number, data: Partial<CrmCompany>): Promise<ApiResponse<{ company: CrmCompany }>> => {
    const response = await api.put(`/crm/companies/${id}`, data);
    return response.data;
  },

  deleteCompany: async (id: number): Promise<ApiResponse> => {
    const response = await api.delete(`/crm/companies/${id}`);
    return response.data;
  },

  searchEntities: async (entityType: CrmLink['entity_type'], search: string): Promise<ApiResponse<{ items: Array<{ id: number; label: string; meta?: string | null }> }>> => {
    const response = await api.get('/crm/entities', { params: { entityType, search } });
    return response.data;
  },

  createLink: async (data: { contactId?: number | null; companyId?: number | null; entityType: CrmLink['entity_type']; entityId: number }): Promise<ApiResponse<{ link: CrmLink }>> => {
    const response = await api.post('/crm/links', data);
    return response.data;
  },

  deleteLink: async (id: number): Promise<ApiResponse> => {
    const response = await api.delete(`/crm/links/${id}`);
    return response.data;
  }
};
